import { useState } from 'react';
import { Button, Portal, Select } from '@nazka/nazka.mapframe.components';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import CloseButton from '@App/components/CloseButton/CloseButton';
import ConfirmationDialog from '@App/components/ConfirmationDialog/ConfirmationDialog';

import { useCompanyUsers, useUserCompanies } from '@services/query.service';
import { useAuth } from '@App/components/AuthProvider/auth-provider.context';

import queryClient from '@App/app.query-client';

import { adminRemoveUserFromCompany } from '@services/admin.service';
import AdminCreateUser from './AdminCreateUser/AdminCreateUser';

import './admin-user-management.styl';

function AdminUserManagement({ onClose }) {
  const { user: currentUser } = useAuth();

  const [selectedCompany, setSelectedCompany] = useState(undefined);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [removeUserWarningData, setRemoveUserWarningData] = useState(false);

  const { data: userCompanies } = useUserCompanies();
  const currentlySelected = selectedCompany || userCompanies[0]?.OND_MZ;
  const { data: companyUsers } = useCompanyUsers(currentlySelected);

  const removeUserFromCompanyMutation = useMutation({
    mutationFn: userId => adminRemoveUserFromCompany(userId, currentlySelected),
    onSuccess: () => {
      queryClient.invalidateQueries(['company', 'users', currentlySelected]);
      toast.success('Gebruikerstoegang verwijderd');
    },
    onError: () => {
      toast.error('Er ging iets mis');
    },
  });

  return (
    <Portal>
      <div className="admin-user-management">
        <div className="admin-user-management__content">
          <h3 className="admin-user-management__content__title">
            Gebruikersbeheer
            <CloseButton className="admin-user-management__content__title__close" onClick={showCreateUserForm ? () => setShowCreateUserForm(false) : onClose} />
          </h3>
          <div className="admin-user-management__content__body">
            {showCreateUserForm ? (
              <AdminCreateUser
                onClose={() => setShowCreateUserForm(false)}
                companyId={currentlySelected}
              />
            ) : (
              <>
                <Select
                  className="admin-user-management__content__body__users__select"
                  options={userCompanies.map(({ OND_MZ, NAZKA_NM }) => ({
                    id: OND_MZ,
                    label: NAZKA_NM || OND_MZ,
                  }))}
                  value={currentlySelected}
                  onInputChange={option => setSelectedCompany(option.id)}
                />
                <div className="admin-user-management__content__body__users">
                  <table>
                    <thead>
                      <tr>
                        <th>Voornaam</th>
                        <th>Achternaam</th>
                        <th>Email</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {companyUsers?.map((user) => {
                        const isCurrentUser = user.sub === currentUser.sub;

                        return (
                          <tr key={user.sub}>
                            <td>
                              {user.given_name}
                            </td>
                            <td>
                              {user.family_name}
                            </td>
                            <td>
                              {user.email}
                            </td>
                            <td>
                              <Button
                                layout="ghost"
                                hover={false}
                                icon={faTrash}
                                color="black"
                                disabled={isCurrentUser || companyUsers.length === 1}
                                onClick={() => setRemoveUserWarningData(user)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Button
                  onClick={() => setShowCreateUserForm(true)} 
                  className="admin-user-management__content__body__button"
                >
                  Voeg een gebruiker toe
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {removeUserWarningData && (
        <ConfirmationDialog
          title={`Ben je zeker dat je de toegang voor ${removeUserWarningData.given_name} ${removeUserWarningData.family_name} wilt verwijderen?`}
          onClose={() => setRemoveUserWarningData(false)}
          onConfirm={() => {
            removeUserFromCompanyMutation.mutate(removeUserWarningData.sub);
            setRemoveUserWarningData(false);
          }}
        />
      )}
    </Portal>
  );
}

export default AdminUserManagement;